import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  SimpleGrid,
  Icon,
  useColorModeValue,
  Divider,
  List,
  ListItem,
  ListIcon,
  Badge,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaIndustry,
  FaPlane,
  FaFlask,
  FaDollarSign,
  FaCube,
  FaClock,
  FaMicrochip,
  FaFire,
  FaChartLine,
  FaBolt,
  FaWind,
  FaAtom,
} from 'react-icons/fa';
import Footer from '../Footer';

const ShockBubbleInteraction = () => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');
  const [showAnimation, setShowAnimation] = useState(false);

  return (
    <Box minH="100vh" bg="gray.50">
      <Helmet>
        <title>Shock–Bubble Interaction: Advanced Multiphase Shockwave Dynamics | SRS Blog</title>
        <meta name="description" content="When a high-speed shockwave meets a lighter gas bubble, the Richtmyer–Meshkov instability drives a complex multiphase interaction. Explore the physics with SRS." />
        <link rel="canonical" href="https://srs.numericalai.net/blog/shock-bubble-interaction" />
        <meta property="og:title" content="Shock–Bubble Interaction: Advanced Multiphase Shockwave Dynamics | SRS Blog" />
        <meta property="og:description" content="When a high-speed shockwave meets a lighter gas bubble, the Richtmyer–Meshkov instability drives a complex multiphase interaction. Explore the physics with SRS." />
        <meta property="og:url" content="https://srs.numericalai.net/blog/shock-bubble-interaction" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Shock–Bubble Interaction: Advanced Multiphase Shockwave Dynamics | SRS Blog" />
        <meta name="twitter:description" content="When a high-speed shockwave meets a lighter gas bubble, the Richtmyer–Meshkov instability drives a complex multiphase interaction. Explore the physics with SRS." />
      </Helmet>
      {/* Header */}
      <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200">
        <Container maxW="7xl" py={4}>
          <HStack justify="space-between">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image 
                src="/simple-logo.png" 
                alt="SRS Logo" 
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="lg" color="brand.500">SRS</Heading>
                <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </HStack>
        </Container>
      </Box>

      <Container maxW="5xl" py={12}>
        <VStack spacing={12} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center">
            <Badge colorScheme="cyan" fontSize="md" mb={4} px={3} py={1}>
              Multiphase Dynamics
            </Badge>
            <Heading 
              as="h1"
              size="2xl" 
              color="brand.500" 
              mb={6}
              fontWeight="bold"
            >
              Shock-Bubble Interaction
            </Heading>
            <Heading 
              size="lg" 
              color="gray.600" 
              mb={6}
              fontWeight="medium"
            >
              Advanced Multiphase Shockwave Dynamics
            </Heading>
            
            <Text 
              fontSize="xl" 
              color="gray.700" 
              lineHeight="tall"
              mb={6}
              textAlign="left"
            >
              When a high-speed shockwave travels through a gas and meets a lighter medium like a bubble, it creates a complex and beautiful interaction of physics governed by the <strong>Richtmyer-Meshkov instability</strong>.
            </Text>
            
            <Text 
              fontSize="lg" 
              color="gray.600" 
              lineHeight="tall"
              textAlign="left"
            >
              Using NumericalAI, you can visualize, predict, and optimize these dynamic flow events across industries where precision and safety matter most. The simulation below shows how a shockwave travels and deforms a helium bubble suspended in air — capturing every ripple, reflection, and distortion in stunning detail.
            </Text>
          </Box>

          {/* Animation Toggle */}
          <Box textAlign="center">
            <Button
              size="lg"
              colorScheme={showAnimation ? "gray" : "brand"}
              onClick={() => setShowAnimation(!showAnimation)}
              leftIcon={<FaWind />}
            >
              {showAnimation ? "Show Time Snapshots" : "Show Full Animation"}
            </Button>
          </Box>

          {/* Images or Animation */}
          {showAnimation ? (
            <Box 
              bg={cardBg}
              borderRadius="xl"
              overflow="hidden"
              shadow="2xl"
              border="1px"
              borderColor={borderColor}
            >
              <Image 
                src={require('../../img/blogs/2D_ShockBubble_Vel_Animation_NumericalAI.gif')}
                alt="Shock-Bubble Interaction Animation"
                w="100%"
                objectFit="contain"
                bg="gray.100"
              />
              <Box p={6} bg={sectionBg}>
                <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2} textAlign="center">
                  <strong>Complete Temporal Evolution</strong>
                </Text>
                <Text fontSize="md" color="gray.600" lineHeight="tall" textAlign="center">
                  Full animation showing the shock wave interaction with the helium bubble. The sequence captures initial impact, bubble compression, Richtmyer-Meshkov instability development, vortex ring formation, and downstream mixing — a complete demonstration of compressible multiphase flow physics.
                </Text>
              </Box>
            </Box>
          ) : (
            <Tabs variant="enclosed" colorScheme="brand">
              <TabList bg={sectionBg} borderRadius="xl" p={2} flexWrap="wrap">
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> t = 0.1 ms
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> t = 0.24 ms
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> t = 0.32 ms
                </Tab>
                <Tab _selected={{ color: 'brand.500', bg: 'white' }} fontSize={{ base: 'xs', md: 'sm' }}>
                  <Icon as={FaWind} mr={2} /> t = 0.49 ms
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_ShockBubble_Vel_contour_time_0_1ms_NumericalAI.jpg')}
                      alt="Shock-Bubble at 0.1ms"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Early Stage (t = 0.1 ms)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        The planar shock wave (Mach ~1.22) approaches the cylindrical helium bubble from the left. The undisturbed bubble maintains its circular shape, about to experience the initial shock impact. The velocity field shows the uniform shock propagation through the ambient air before interaction begins.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_ShockBubble_Vel_contour_time_0_24ms_NumericalAI.jpg')}
                      alt="Shock-Bubble at 0.24ms"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Compression & Deformation (t = 0.24 ms)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        The shock has impacted the bubble interface. Due to the acoustic impedance mismatch (helium is lighter than air), the transmitted shock inside the bubble moves faster than the incident shock. The bubble begins to compress and deform, with the upstream interface flattening. A refracted shock propagates into the bubble while a reflected expansion wave moves back into the air.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_ShockBubble_Vel_contour_time_0_32ms_NumericalAI.jpg')}
                      alt="Shock-Bubble at 0.32ms"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Instability Development (t = 0.32 ms)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        Flow instabilities develop at the interface between the two gases. The <strong>Richtmyer-Meshkov instability</strong> manifests as interfacial perturbations grow due to impulsive acceleration from the shock. The bubble has deformed into a kidney-like shape. Vorticity is generated at the interface through baroclinic production (misalignment of pressure and density gradients).
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>

                <TabPanel p={0}>
                  <Box 
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    shadow="2xl"
                    border="1px"
                    borderColor={borderColor}
                    mt={4}
                  >
                    <Image 
                      src={require('../../img/blogs/2D_ShockBubble_Vel_contour_time_0_49ms_NumericalAI.jpg')}
                      alt="Shock-Bubble at 0.49ms"
                      w="100%"
                      objectFit="contain"
                      bg="gray.100"
                    />
                    <Box p={6} bg={sectionBg}>
                      <Text fontSize="lg" color="gray.700" fontWeight="semibold" mb={2}>
                        <strong>Vortex Formation & Mixing (t = 0.49 ms)</strong>
                      </Text>
                      <Text fontSize="md" color="gray.600" lineHeight="tall">
                        A counter-rotating vortex pair forms at the bubble interface, rolling up the helium into complex structures. The upstream portion of the bubble has developed a jet-like protrusion, while the downstream side exhibits mushroom-shaped features. Intense mixing occurs at the interface as helium and air intertwine. The shock continues propagating downstream, having deposited vorticity and energy into the bubble.
                      </Text>
                    </Box>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}

          <Divider />

          {/* Physics Background */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              The Richtmyer-Meshkov Instability
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Text fontSize="lg" color="gray.700" lineHeight="tall" mb={4}>
                  The <strong>Richtmyer-Meshkov (RM) instability</strong> occurs when a shock wave passes through a perturbed interface between fluids of different densities. Named after Robert Richtmyer (1960) and Evgeny Meshkov (1969), this instability is fundamental to:
                </Text>
                <List spacing={3}>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Inertial Confinement Fusion (ICF)</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        RM instabilities at the fuel capsule interface can prevent successful fusion ignition, making their prediction critical for ICF facility design.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Supernova Explosions</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Astrophysicists study RM instabilities to understand mixing in supernova remnants and star formation processes.
                      </Text>
                    </Box>
                  </ListItem>
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                    <Box>
                      <Text fontSize="md" fontWeight="semibold" color="gray.800">Scramjet Combustion</Text>
                      <Text fontSize="md" color="gray.600" mt={1}>
                        Enhanced mixing from RM instabilities improves fuel-air mixing in supersonic combustion ramjets.
                      </Text>
                    </Box>
                  </ListItem>
                </List>
              </Box>

              <Box bg="cyan.50" p={6} borderRadius="xl" border="2px" borderColor="cyan.200">
                <Heading size="md" color="cyan.800" mb={3}>
                  Why This Test Case Matters
                </Heading>
                <Text fontSize="md" color="cyan.900" lineHeight="tall">
                  The shock-bubble problem is a <strong>canonical benchmark</strong> for validating compressible multiphase solvers. It tests the code's ability to: (1) capture sharp discontinuities (shocks), (2) maintain interface sharpness between fluids, (3) correctly predict wave speeds in different materials, and (4) resolve complex flow features like vortices and mixing layers. NumericalAI's results match published experimental and computational data from leading research groups.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Simulation Configuration */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Simulation Configuration
            </Heading>
            
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6} mb={8}>
              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaCube} boxSize={6} color="brand.500" />
                    <Heading size="md" color="gray.800">Domain & Grid</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Formulation:</Text>
                      <Text fontWeight="semibold">2D Cartesian</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Domain Size:</Text>
                      <Text fontWeight="semibold">450 mm × 89 mm</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Grid Resolution:</Text>
                      <Text fontWeight="semibold">890 × 178</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Total Cells:</Text>
                      <Text fontWeight="semibold">158,420</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Bubble Diameter:</Text>
                      <Text fontWeight="semibold">50 mm</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaAtom} boxSize={6} color="cyan.500" />
                    <Heading size="md" color="gray.800">Fluid Properties</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Ambient (Air):</Text>
                      <Text color="gray.600" fontSize="sm">ρ = 1.225 kg/m³, γ = 1.4</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="semibold" color="gray.700" mb={1}>Bubble (Helium):</Text>
                      <Text color="gray.600" fontSize="sm">ρ = 0.166 kg/m³, γ = 1.648</Text>
                    </Box>
                    <HStack justify="space-between">
                      <Text color="gray.600">Density Ratio:</Text>
                      <Text fontWeight="semibold">~7.4:1 (air:helium)</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Atwood Number:</Text>
                      <Text fontWeight="semibold">A ≈ 0.76</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaBolt} boxSize={6} color="red.500" />
                    <Heading size="md" color="gray.800">Shock Conditions</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Shock Mach:</Text>
                      <Text fontWeight="semibold">M = 1.22</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Shock Velocity:</Text>
                      <Text fontWeight="semibold">~419 m/s</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Pressure Jump:</Text>
                      <Text fontWeight="semibold">~1.6×</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Initial Position:</Text>
                      <Text fontWeight="semibold">x = 150 mm</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor} h="100%">
                  <HStack spacing={3} mb={4}>
                    <Icon as={FaChartLine} boxSize={6} color="purple.500" />
                    <Heading size="md" color="gray.800">Numerical Methods</Heading>
                  </HStack>
                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text color="gray.600">Spatial Scheme:</Text>
                      <Text fontWeight="semibold">WENO5</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Time Integration:</Text>
                      <Text fontWeight="semibold">RK3-TVD</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Riemann Solver:</Text>
                      <Text fontWeight="semibold">HLLC</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Interface Capture:</Text>
                      <Text fontWeight="semibold">5-equation model</Text>
                    </HStack>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>

            <Box bg="cyan.50" p={6} borderRadius="xl" border="2px" borderColor="cyan.200">
              <HStack spacing={3} mb={3}>
                <Icon as={FaMicrochip} boxSize={6} color="cyan.600" />
                <Heading size="md" color="cyan.800">Computational Performance</Heading>
              </HStack>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="cyan.600">~11 secs</Text>
                  <Text fontSize="sm" color="gray.600">Wall-Clock Time</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="cyan.600">A100</Text>
                  <Text fontSize="sm" color="gray.600">NVIDIA GPU</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="cyan.600">~700 MB</Text>
                  <Text fontSize="sm" color="gray.600">Memory Usage</Text>
                </Box>
                <Box bg="white" p={4} borderRadius="lg" textAlign="center">
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">~11×</Text>
                  <Text fontSize="sm" color="gray.600">Speedup vs CPU</Text>
                </Box>
              </SimpleGrid>
            </Box>
          </Box>

          <Divider />

          {/* Physics Insights */}
          <Box>
            <Heading size="xl" color="brand.500" mb={6}>
              Key Physics Phenomena
            </Heading>
            
            <VStack spacing={6} align="stretch">
              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Acoustic Impedance Mismatch
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={4}>
                  The key to understanding shock-bubble interaction is <strong>acoustic impedance</strong> (Z = ρc, where ρ is density and c is sound speed):
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      Air: Z_air ≈ 428 Pa·s/m
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      Higher impedance due to greater density
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" color="gray.800" mb={2}>
                      Helium: Z_He ≈ 164 Pa·s/m
                    </Text>
                    <Text color="gray.600" fontSize="sm" lineHeight="tall">
                      Lower impedance despite higher sound speed
                    </Text>
                  </Box>
                </SimpleGrid>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mt={4}>
                  When the shock encounters the low-impedance helium, part of the shock transmits (moving faster in helium due to higher sound speed) while part reflects as an <strong>expansion wave</strong> back into the air. This reflection reverses flow direction locally, initiating the bubble's inward collapse.
                </Text>
              </Box>

              <Box bg={cardBg} p={6} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
                <Heading size="md" color="gray.800" mb={4}>
                  Baroclinic Vorticity Generation
                </Heading>
                <Text fontSize="md" color="gray.700" lineHeight="tall" mb={3}>
                  The vorticity equation includes a baroclinic production term:
                </Text>
                <Box bg={sectionBg} p={4} borderRadius="md" fontFamily="monospace" fontSize="sm" mb={3}>
                  Dω/Dt = (1/ρ²) ∇ρ × ∇p + ...
                </Box>
                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  When pressure and density gradients are not aligned (as at the curved shock-bubble interface), vorticity is generated. This deposited vorticity rolls up into the counter-rotating vortex pair visible in the late-time images, driving mixing and turbulent transition.
                </Text>
              </Box>
            </VStack>
          </Box>

          <Divider />

          {/* Industrial Applications */}
          <Box>
            <Heading size="lg" color="brand.500" mb={6} textAlign="center">
              Industrial Applications
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaFire} boxSize={8} color="orange.500" />
                  <Heading size="md" color="gray.800">
                    Energy & Power Systems
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Gas mixing optimization, detonation wave analysis, and combustion chamber design. Understanding shock-induced mixing improves fuel injection strategies and reduces emissions in gas turbines and engines.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaPlane} boxSize={8} color="blue.500" />
                  <Heading size="md" color="gray.800">
                    Aerospace & Defense
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Shockwave–fuel interactions in scramjets, blast-wave propagation for protective structure design, and inert gas mixing studies. Critical for hypersonic vehicle development and explosion safety analysis.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaFlask} boxSize={8} color="purple.500" />
                  <Heading size="md" color="gray.800">
                    Research & Development
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Flow visualization for high-speed impact studies, validation of multiphase CFD codes, and fundamental instability research. Essential for advancing our understanding of shock physics and multimaterial flows.
                  </Text>
                </VStack>
              </Box>

              <Box 
                bg={cardBg}
                p={6}
                borderRadius="xl"
                shadow="md"
                border="1px"
                borderColor={borderColor}
                _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                transition="all 0.2s"
              >
                <VStack spacing={3} align="start">
                  <Icon as={FaIndustry} boxSize={8} color="red.500" />
                  <Heading size="md" color="gray.800">
                    Process & Manufacturing
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    Shock wave lithotripsy modeling, ultrasonic cleaning optimization, and explosive forming process design. Predict material response and mixing efficiency in shock-driven industrial processes.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          {/* Why NumericalAI */}
          <Box bg={cardBg} p={8} borderRadius="xl" shadow="md" border="1px" borderColor={borderColor}>
            <Heading size="lg" color="brand.500" mb={6}>
              Why NumericalAI for Shock-Driven Multiphase Flows
            </Heading>
            <List spacing={4}>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Real-world physics, virtually recreated</strong> — Visualize shockwaves, vortices, and flow patterns in detail impossible to capture experimentally
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>High performance, scalable simulations</strong> — Designed for both desktop and HPC environments with GPU acceleration
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Faster insights, smarter design</strong> — Reduce testing time and accelerate innovation safely through virtual prototyping
                </Text>
              </ListItem>
              <ListItem display="flex" alignItems="flex-start">
                <ListIcon as={FaCheckCircle} color="green.500" mt={1} boxSize={5} />
                <Text fontSize="lg" color="gray.700" lineHeight="tall">
                  <strong>Validated compressible multiphase solver</strong> — Results match experimental shadowgraphs and published benchmarks from leading research institutions
                </Text>
              </ListItem>
            </List>
          </Box>

          {/* Business Value */}
          <Box 
            bg="brand.500" 
            color="white" 
            p={8} 
            borderRadius="xl" 
            shadow="xl"
            position="relative"
            overflow="hidden"
          >
            <Box position="relative" zIndex={1}>
              <HStack spacing={3} mb={4}>
                <Icon as={FaDollarSign} boxSize={8} />
                <Heading size="lg">Business Value & ROI</Heading>
              </HStack>
              <Text fontSize="lg" lineHeight="tall" mb={4}>
                Traditional shock tube experiments cost thousands of dollars per test and provide limited data (single-point pressure measurements or schlieren images). NumericalAI delivers <strong>full-field data</strong> — pressure, velocity, density, temperature, and vorticity — everywhere in the domain, at every time step.
              </Text>
              <Text fontSize="lg" lineHeight="tall">
                <strong>Cost Reduction:</strong> Replace dozens of expensive experiments with parametric simulation studies. Test multiple gas combinations, shock strengths, and geometries virtually before committing to hardware. Accelerate product development while reducing prototyping costs by 70%+.
              </Text>
            </Box>
            
            {/* Decorative background */}
            <Box
              position="absolute"
              top="-50%"
              right="-10%"
              width="300px"
              height="300px"
              borderRadius="full"
              bg="whiteAlpha.100"
              zIndex={0}
            />
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" py={8}>
            <VStack spacing={4}>
              <Heading size="md" color="gray.700">
                Ready to Explore Shock-Driven Multiphase Flows?
              </Heading>
              <Text color="gray.600" fontSize="lg" maxW="2xl">
                Run your own shock-bubble simulations on NumericalAI's cloud platform. Visualize complex physics, validate designs, and accelerate innovation.
              </Text>
              <HStack spacing={4} mt={4}>
                <Button
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/start-simulation')}
                >
                  Start Your Simulation
                </Button>
                <Button
                  variant="outline"
                  colorScheme="brand"
                  size="lg"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

export default ShockBubbleInteraction;